import { IonCard, IonSpinner } from '@ionic/react'
import { Chart as ChartJS, registerables } from 'chart.js'
import annotationPlugin from 'chartjs-plugin-annotation'
import React, { useRef, useState, useMemo, useContext, useEffect, useCallback } from 'react'
import { Chart } from 'react-chartjs-2'
import { ChartJSOrUndefined } from 'react-chartjs-2/dist/types'
import { useTranslation } from 'react-i18next'
import dayjs, { Dayjs } from 'dayjs'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import weekday from 'dayjs/plugin/weekday'

import { AppContext } from '../../../contexts/AppContext'
import useUser from '../../../contexts/hooks/useUser'
import useTrackers from '../../../modals/trackerModal/hooks/useTrackers'
import usePreference from '../../../utils/preferences/usePreference'
import TrackerCard from '../components/TrackerCard'
import BaseTooltip, { TooltipData } from '../components/baseTooltip/BaseTooltip'
import { useMeanAggregatedDayAheadPrices } from '../../../hooks/dayAheadPrices/useMeanAggregatedDayAheadPrices'
import { formatTimePeriod, formatWeekDay, nextPeriod, previousPeriod, spanFromPeriod, TimePeriod, timePeriodId, TimePeriodResolution } from '../../../utils/timePeriod'
import { meanAvg } from '../../../utils/math'
import { defaultDataResolutionForTimePeriodResolution } from '../utils/defaultDataResolutionForTimePeriodResolution'
import { getPriceChartOptions } from '../charts/priceChart/options/priceChartOptions'
import { getTrackerColorForValue } from '../utils/getTrackerColorForValue'
import ChartScroller from '../components/ChartScoller'
import QuarterHourlyToggle from '../charts/priceChart/QuarterHourlyToggle'
import ChartTimeSwitch from '../components/ChartTimeSwitch2'
import quarterHour from '../../../utils/quarterHourDayjsPlugin/quarterHour'
import { SpotPrice, useDayAheadPrices } from '../../../hooks/dayAheadPrices/useDayAheadPrices'
import HourlyPrice from '../components/hourlyPrice2/HourlyPrice2'
import OurProductsCard from '../../home/components/OurProductsCard'

dayjs.extend(quarterHour)
dayjs.extend(weekOfYear)
dayjs.extend(weekday)

ChartJS.register(...registerables, annotationPlugin)

/**
 * Check if a day's prices have enough records to show the day on UI.
 * 
 * 12:00 (=48 quarter-hours) was chosen as arbitrary threshold for showing/hiding.
 * For example if someone only had 4 hours worth of data for tomorrow,
 * then tomorrow would not be shown for them.
 * 
 * The amount of data records can vary based on the current timezone of the client.
 */
const hasEnoughPricesToShow = (dayAheadPrices: SpotPrice[]) => dayAheadPrices.length > 48

export interface DayAheadPricesTabProps {
	anchorRef: React.RefObject<HTMLDivElement>
}

const DayAheadPricesTab = ({ anchorRef }: DayAheadPricesTabProps) => {
	const { t } = useTranslation('pages_electricity')
	const { isDarkMode } = useContext(AppContext)
	const { user } = useUser()

	const [timePeriod, setTimePeriod] = useState<TimePeriod>({
		resolution: 'day',
		timestamp: dayjs(),
	})

	const [quarterHourlyPreference, setPreferQuarterHourlyResolution] = usePreference(
		'preferredChartModePriceChart',
		'hour'
	)

	const dataResolution: TimePeriodResolution = useMemo(() => {
		if (timePeriod.resolution === 'day') {
			return quarterHourlyPreference === 'quarter' ? 'quarterHour' : 'hour'
		}
		return defaultDataResolutionForTimePeriodResolution(timePeriod.resolution)
	}, [timePeriod.resolution, quarterHourlyPreference])

	const optimizedSpan = useCallback((from: Dayjs, to: Dayjs): [Dayjs, Dayjs] => {
		// If the user is looking at a nearby dates (majority of the use cases),
		// just fetch few days worth of data.
		if (timePeriod.resolution === 'day') {
			const twoDaysAgo = dayjs().subtract(2, 'day').startOf('day')
			const tomorrow = dayjs().add(1, 'day').endOf('day')
			if (from.isSameOrAfter(twoDaysAgo) && to.isSameOrBefore(tomorrow)) {
				return [twoDaysAgo, tomorrow]
			}
		}

		// Once the user starts "browsing around", fetch the whole year to cache. 
		return [from.startOf('year'), to.endOf('year')]
	}, [timePeriod.resolution])

	const [from, to] = useMemo(() => spanFromPeriod(timePeriod), [timePeriodId(timePeriod)])
	const { dayAheadPrices, isLoading: dayAheadPricesLoading } = useMeanAggregatedDayAheadPrices({
		from, to,
		targetResolution: dataResolution,
		cacheSpan: optimizedSpan(from, to),
	})

	const [activeElementIndex, _setActiveElementIndex] = useState(0)

	const setActiveElementIndex = useCallback((index: number) => {
		if (index <= 0) {
			index = 0
		} else if (index >= dayAheadPrices.length - 1) {
			index = dayAheadPrices.length - 1
		}
		if (index === activeElementIndex) {
			return
		}
		_setActiveElementIndex(index)

		// This enables functionality where if "August" is active in the chart,
		// then clicking "Month view" will open August
		setTimePeriod(tp => ({
			resolution: tp.resolution,
			timestamp: dayAheadPrices[index].deliveryStart,
		}))
	}, [activeElementIndex, dayAheadPrices, timePeriod.resolution])

	useEffect(() => {
		if (dayAheadPrices.length === 0) {
			return
		}
		if (activeElementIndex > dayAheadPrices.length - 1) {
			setActiveElementIndex(dayAheadPrices.length - 1)
		}
	}, [activeElementIndex, dayAheadPrices.length])

	const barRef = useRef<ChartJSOrUndefined<any, string[] | number[], any>>(null)
	const [useBarGraph, setUseBarGraph] = useState(true)

	const priceTrackers = useTrackers()
	const enabledPriceTrackers = useMemo(
		() => priceTrackers.data?.filter(tracker => tracker.enabled) ?? [],
		[priceTrackers],
	)
	const averagePrice = useMemo(
		() => meanAvg(dayAheadPrices.map(price => price.centsPerKwh)),
		[dayAheadPrices],
	)

	const [nextFrom, nextTo] = useMemo(() => spanFromPeriod(nextPeriod(timePeriod)), [timePeriodId(timePeriod)])
	const { dayAheadPrices: nextDayAheadPrices } = useDayAheadPrices({
		from: nextFrom,
		to: nextTo,
		cacheSpan: optimizedSpan(nextFrom, nextTo),
	})

	const [prevFrom, prevTo] = useMemo(() => spanFromPeriod(previousPeriod(timePeriod)), [timePeriodId(timePeriod)])
	const { dayAheadPrices: prevDayAheadPrices } = useDayAheadPrices({
		from: prevFrom,
		to: prevTo,
		cacheSpan: optimizedSpan(prevFrom, prevTo),
	})

	const chartLabels = useMemo(() => {
		if (timePeriod.resolution === 'week') {
			// 'day' is typically formatted as D.M. so we need 
			return dayAheadPrices.map(price => formatWeekDay(price.deliveryStart))
		}
		return dayAheadPrices.map(price => formatTimePeriod({
			resolution: dataResolution,
			timestamp: price.deliveryStart,
		}))
	}, [dayAheadPrices, timePeriod.resolution, dataResolution])

	const chartValues = useMemo(
		() => dayAheadPrices.map(price => price.centsPerKwh),
		[dayAheadPrices]
	)

	const handleChartClick = (event: any) => {
		if (!barRef.current) {
			return
		}
		const index = barRef.current.scales.x.getValueForPixel(event.nativeEvent.offsetX)
		setActiveElementIndex(index ?? 0)
	}

	const tooltipData: TooltipData[] = useMemo(() => {
		if (!dayAheadPrices || dayAheadPrices.length <= activeElementIndex) {
			return []
		}
		const price = dayAheadPrices[activeElementIndex].centsPerKwh
		return [
			{
				label: t(timePeriod.resolution === 'day' ? 'hinta' : 'averagePrice'),
				value: price,
				color: '#ffb30d',
				overrideUnit: t('centsPerKWh'),
			},
		]
	}, [activeElementIndex, t, dayAheadPrices, timePeriod.resolution])

	const options = useMemo(
		() => getPriceChartOptions({
			activeElement: activeElementIndex,
			displayedTrackers: enabledPriceTrackers,
			xLabels: chartLabels,
			isDarkMode,
			onHover: setActiveElementIndex,
			annotations: [],
		}),
		[activeElementIndex, isDarkMode, chartLabels, enabledPriceTrackers]
	)

	const datasets = useMemo(() => {
		if (useBarGraph) {
			return [
				{
					id: 'price',
					data: chartValues,
					label: t('hinta'),
					backgroundColor: chartValues.map(price => getTrackerColorForValue(priceTrackers, price)),
					borderRadius: 2,
					pointRadius: 0,
				}
			]
		} else {
			return [
				{
					id: 'price',
					data: chartValues,
					label: t('hinta'),
					backgroundColor: chartValues.map(price => getTrackerColorForValue(priceTrackers, price)),
					borderRadius: 2,
					borderWidth: 3,
					borderColor: '#5c95ff',
					touchRadius: 6,
					pointRadius: quarterHourlyPreference === 'quarter' ? 1 : 3,
				}
			]
		}
	}, [t, priceTrackers, chartValues, useBarGraph, quarterHourlyPreference])

	const handleTimePeriodChange = (newTimePeriod: TimePeriod) => {
		setTimePeriod(newTimePeriod)
	}

	return (
		<div className='m-auto flex flex-col justify-center gap-md w-full'>
			<div ref={anchorRef}>
				<ChartScroller
					timePeriod={timePeriod}
					setTimePeriod={setTimePeriod}
					tooltipLoading={dayAheadPricesLoading}
					value={averagePrice}
					unit={t('priceLabel')}
					canGoNext={hasEnoughPricesToShow(nextDayAheadPrices)}
					canGoPrevious={hasEnoughPricesToShow(prevDayAheadPrices)}
				/>
			</div>

			<div className={`grid ${dayAheadPricesLoading ? 'loading' : ''} grid-cols-1 2xl:grid-cols-2 gap-4`}>
				<div className='flex flex-col gap-4'>
					<IonCard id='chart' className='w-full rounded-b-[16px] bg-transparent shadow-card'>
						<BaseTooltip
							isLoading={dayAheadPricesLoading}
							tooltipData={tooltipData}
						/>
						<QuarterHourlyToggle
							showQuarterHourlyToggle={dataResolution === 'quarterHour' || dataResolution === 'hour'}
							showQuarterHourly={quarterHourlyPreference === 'quarter'}
							onChangeQuarterHourly={(value: boolean) =>
								setPreferQuarterHourlyResolution(value ? 'quarter' : 'hour')
							}
							showBarGraph={useBarGraph}
							onChangeBarGraph={setUseBarGraph}
							infoText={t('quarterHourlyToggle.electricityInfo')}
						/>
						<div
							className={`canvas-container border bg-white border-[#d7d7d7] dark:border-brand-secondary-200-dark dark:bg-[#063a3e]`}
						>
							{dayAheadPricesLoading ? (
								<div>
									<IonSpinner />
								</div>
							) : (
								<Chart
									ref={barRef}
									type={useBarGraph ? 'bar' : 'line'}
									options={options}
									data={{
										labels: chartLabels,
										datasets,
									}}
									onClick={handleChartClick}
								/>
							)}
						</div>

						<ChartTimeSwitch
							timePeriod={timePeriod}
							setTimePeriod={handleTimePeriodChange}
						/>
					</IonCard>
					{user !== null && <TrackerCard />}
				</div>

				<div className='flex flex-col gap-4'>
					{!user && <OurProductsCard />}
					<HourlyPrice
						timePeriod={timePeriod}
						dayAheadPrices={dayAheadPrices}
						dataResolution={dataResolution}
						isLoading={dayAheadPricesLoading}
					/>
				</div>
			</div>
		</div>
	)
}

export default DayAheadPricesTab
